import { useEffect, useMemo, useState } from 'react';
import BanquetHelpPanel from './BanquetHelpPanel';

function BanquetPayments({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const [bookings, setBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_mode: 'CASH', payment_type: 'ADVANCE', receipt_no: '', gateway_ref: '' });
  const [refundForm, setRefundForm] = useState({ refund_amount: '', reason: '', refund_mode: 'BANK_TRANSFER', reference_no: '' });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadBookings = async () => {
    const res = await fetch('/api/v1/banquet/bookings', { headers });
    const { data, error: apiError } = await parseJson(res);
    if (!res.ok) throw new Error(apiError || 'Failed to load bookings');
    const list = Array.isArray(data) ? data : [];
    setBookings(list);
    if (!selectedBookingId && list.length) setSelectedBookingId(String(list[0].booking_id));
  };

  const loadPaymentsAndRefunds = async (bookingId) => {
    if (!bookingId) {
      setPayments([]);
      setRefunds([]);
      setLedger([]);
      return;
    }
    const [pRes, rRes, lRes] = await Promise.all([
      fetch(`/api/v1/banquet/bookings/${bookingId}/payments`, { headers }),
      fetch(`/api/v1/banquet/bookings/${bookingId}/refunds`, { headers }),
      fetch(`/api/v1/banquet/finance/ledger?booking_id=${encodeURIComponent(bookingId)}`, { headers })
    ]);
    const p = await parseJson(pRes);
    const r = await parseJson(rRes);
    const l = await parseJson(lRes);
    if (!pRes.ok) throw new Error(p.error || 'Failed to load payments');
    if (!rRes.ok) throw new Error(r.error || 'Failed to load refunds');
    if (!lRes.ok) throw new Error(l.error || 'Failed to load ledger');
    setPayments(Array.isArray(p.data) ? p.data : []);
    setRefunds(Array.isArray(r.data) ? r.data : []);
    setLedger(Array.isArray(l.data) ? l.data : []);
  };

  useEffect(() => {
    if (!chainId || !propertyId) return;
    loadBookings().catch((err) => setError(err?.message || 'Failed to load bookings'));
  }, [chainId, propertyId, outletId]);

  useEffect(() => {
    loadPaymentsAndRefunds(selectedBookingId).catch((err) => setError(err?.message || 'Failed to load payment data'));
  }, [selectedBookingId]);

  const postPayment = async () => {
    setError('');
    setMessage('');
    if (!selectedBookingId || !paymentForm.amount) return setError('Booking and amount required');
    try {
      const res = await fetch(`/api/v1/banquet/bookings/${selectedBookingId}/payments`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Number(paymentForm.amount),
          payment_mode: paymentForm.payment_mode,
          payment_type: paymentForm.payment_type,
          receipt_no: paymentForm.receipt_no || null,
          gateway_ref: paymentForm.gateway_ref || null
        })
      });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Payment failed');
      setMessage(`Payment posted. Total paid: ${Number(data?.totals?.total_paid || 0).toFixed(2)}`);
      setPaymentForm({ amount: '', payment_mode: 'CASH', payment_type: 'ADVANCE', receipt_no: '', gateway_ref: '' });
      await Promise.all([loadBookings(), loadPaymentsAndRefunds(selectedBookingId)]);
    } catch (err) {
      setError(err?.message || 'Payment failed');
    }
  };

  const postRefund = async () => {
    setError('');
    setMessage('');
    if (!selectedBookingId || !refundForm.refund_amount) return setError('Booking and refund amount required');
    try {
      const res = await fetch(`/api/v1/banquet/bookings/${selectedBookingId}/refunds`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refund_amount: Number(refundForm.refund_amount),
          reason: refundForm.reason || null,
          refund_mode: refundForm.refund_mode || null,
          reference_no: refundForm.reference_no || null
        })
      });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Refund failed');
      setMessage(`Refund posted: #${data?.refund_id}`);
      setRefundForm({ refund_amount: '', reason: '', refund_mode: 'BANK_TRANSFER', reference_no: '' });
      await loadPaymentsAndRefunds(selectedBookingId);
    } catch (err) {
      setError(err?.message || 'Refund failed');
    }
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Payments & Ledger</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div style={{ marginBottom: '0.75rem' }}>
        <select value={selectedBookingId} onChange={(e) => setSelectedBookingId(e.target.value)} style={{ margin: '5px' }}>
          <option value="">Select booking</option>
          {bookings.map((b) => <option key={b.booking_id} value={b.booking_id}>#{b.booking_id} - {b.event_name || 'Event'} ({b.booking_status})</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.8rem', marginBottom: '1rem' }}>
        <div className="card card-body">
          <h4>Post Payment</h4>
          <input placeholder="Amount" type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} style={{ margin: '5px' }} />
          <input placeholder="Payment Type" value={paymentForm.payment_type} onChange={(e) => setPaymentForm({ ...paymentForm, payment_type: e.target.value })} style={{ margin: '5px' }} />
          <input placeholder="Payment Mode" value={paymentForm.payment_mode} onChange={(e) => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })} style={{ margin: '5px' }} />
          <input placeholder="Receipt No" value={paymentForm.receipt_no} onChange={(e) => setPaymentForm({ ...paymentForm, receipt_no: e.target.value })} style={{ margin: '5px' }} />
          <input placeholder="Gateway Ref" value={paymentForm.gateway_ref} onChange={(e) => setPaymentForm({ ...paymentForm, gateway_ref: e.target.value })} style={{ margin: '5px' }} />
          <button onClick={postPayment}>Post Payment</button>
        </div>
        <div className="card card-body">
          <h4>Post Refund</h4>
          <input placeholder="Refund Amount" type="number" step="0.01" value={refundForm.refund_amount} onChange={(e) => setRefundForm({ ...refundForm, refund_amount: e.target.value })} style={{ margin: '5px' }} />
          <input placeholder="Reason" value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} style={{ margin: '5px' }} />
          <input placeholder="Refund Mode" value={refundForm.refund_mode} onChange={(e) => setRefundForm({ ...refundForm, refund_mode: e.target.value })} style={{ margin: '5px' }} />
          <input placeholder="Reference No" value={refundForm.reference_no} onChange={(e) => setRefundForm({ ...refundForm, reference_no: e.target.value })} style={{ margin: '5px' }} />
          <button onClick={postRefund}>Post Refund</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: '1rem' }}>
        <div>
          <h4>Payments</h4>
          <ul>{payments.map((p) => <li key={p.payment_id}>{String(p.paid_at || '').slice(0, 10)} | {p.payment_type} | {Number(p.amount || 0).toFixed(2)} ({p.payment_mode})</li>)}</ul>
        </div>
        <div>
          <h4>Refunds</h4>
          <ul>{refunds.map((r) => <li key={r.refund_id}>{String(r.refunded_at || '').slice(0, 10)} | {Number(r.refund_amount || 0).toFixed(2)} | {r.refund_mode}</li>)}</ul>
        </div>
      </div>

      <h4>Ledger Entries</h4>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Txn</th>
              <th style={{ textAlign: 'left' }}>Type</th>
              <th style={{ textAlign: 'left' }}>Account</th>
              <th style={{ textAlign: 'right' }}>Debit</th>
              <th style={{ textAlign: 'right' }}>Credit</th>
            </tr>
          </thead>
          <tbody>
            {ledger.map((l) => (
              <tr key={l.line_id}>
                <td>{l.txn_id}</td>
                <td>{l.txn_type}</td>
                <td>{l.account_code || l.account_name || l.account_id}</td>
                <td style={{ textAlign: 'right' }}>{Number(l.debit || 0).toFixed(2)}</td>
                <td style={{ textAlign: 'right' }}>{Number(l.credit || 0).toFixed(2)}</td>
              </tr>
            ))}
            {!ledger.length ? <tr><td colSpan={5}>No ledger rows.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BanquetPayments;
